/**
 * AskOracle — homepage intelligence-assistant panel. Routes discovery queries
 * into the existing Archive/Oracle search experience (no fabricated answers),
 * and surfaces ORACLE RECOMMENDS — evidence-weighted real dossiers.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Search, Sparkles, ArrowRight } from "lucide-react";
import { resolveImg, SectionHeader, HRail, MiniRow } from "./homeShared";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROMPTS = [
  { id: "cia-trace", label: "Trace the CIA across the Archive" },
  { id: "coldwar-europe", label: "Cold War intelligence operations in Europe" },
  { id: "intel-latam", label: "What connects intelligence agencies and Latin America?" },
  { id: "surveillance-citizens", label: "Surveillance programs turned on citizens" },
];

const GROUP_PATH = { person: "/people", operation: "/operations" };

const RecCard = ({ d }) => (
  <Link
    to={`/topic/${d.slug}`}
    data-testid={`oracle-rec-${d.slug}`}
    className="group block border overflow-hidden w-[250px] md:w-[280px] hover:-translate-y-1 transition-transform duration-300"
    style={{ borderColor: "#2D2A26", background: "#0F0E12" }}
  >
    <div className="relative h-[140px] overflow-hidden" style={{ background: "#131015" }}>
      {d.image_url && (
        <img
          src={resolveImg(d.thumb_url || d.image_url)}
          alt={d.title}
          loading="lazy"
          className="w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-[1.04] transition-all duration-500"
          style={{ filter: "saturate(0.75) contrast(1.05)" }}
        />
      )}
      <div aria-hidden="true" className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 35%, rgba(11,10,13,0.88) 100%)" }} />
      <span className="absolute top-2.5 left-2.5 inline-flex items-center gap-1.5 font-mono text-[8px] uppercase tracking-[0.3em] px-2 py-1 border text-[#E0A526]" style={{ borderColor: "#E0A52655", background: "rgba(11,10,13,0.7)" }}>
        <Sparkles size={9} /> Oracle pick
      </span>
    </div>
    <div className="p-3.5">
      <h3 className="font-heading text-[15px] leading-snug text-[#E4DFD3] group-hover:text-[#D92D20] transition-colors line-clamp-2 min-h-[38px]">
        {d.title}
      </h3>
      <div className="mt-2 flex items-center gap-3 font-mono text-[9px] uppercase tracking-[0.2em] text-[#8A8377]">
        <span>{d.evidence_count} evidence</span>
        <span className="w-1 h-1 rounded-full bg-[#454038]" />
        <span>{d.source_count} sources</span>
      </div>
    </div>
  </Link>
);

const AskOracle = () => {
  const [q, setQ] = useState("");
  const [recs, setRecs] = useState([]);
  const [disc, setDisc] = useState(null);      // discovery result payload
  const [discLoading, setDiscLoading] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    let dead = false;
    axios.get(`${API}/home/oracle-recommends`, { timeout: 12000 })
      .then((r) => { if (!dead) setRecs(r.data?.items || []); })
      .catch(() => {});
    return () => { dead = true; };
  }, []);

  const go = (query) => {
    const s = (query || "").trim();
    if (s) nav(`/archive?search=${encodeURIComponent(s)}`);
  };

  const askDiscovery = async (p) => {
    if (disc?.id === p.id) { setDisc(null); return; } // toggle off
    setDiscLoading(true);
    try {
      const { data } = await axios.get(`${API}/oracle/discovery`, { params: { id: p.id }, timeout: 15000 });
      setDisc(data);
    } catch { setDisc(null); }
    finally { setDiscLoading(false); }
  };

  return (
    <section data-testid="home-ask-oracle" className="border-t" style={{ borderColor: "#2D2A26", background: "#0B0A0D" }}>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-20">
        <div
          className="border relative overflow-hidden p-5 md:p-10"
          style={{
            borderColor: "#3A3630",
            background: "radial-gradient(120% 120% at 15% 0%, rgba(217,45,32,0.07) 0%, transparent 55%), #0F0E12",
          }}
        >
          <div className="flex items-center gap-3 mb-3">
            <span className="h-px w-10" style={{ background: "#D92D20" }} />
            <span className="font-mono text-[9px] uppercase tracking-[0.5em] text-[#D92D20]">
              File 004 · Intelligence Assistant
            </span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl lg:text-[44px] text-[#E4DFD3] leading-[1.05] tracking-tight">
            Ask Oracle
          </h2>
          <p className="mt-2 text-[13px] md:text-[14px] text-[#8A8377]">
            Your intelligence assistant — query the entire evidence network.
          </p>

          <form
            className="mt-5 md:mt-6 flex items-stretch border max-w-2xl focus-within:border-[#D92D20] transition-colors"
            style={{ borderColor: "#3A3630", background: "#0B0A0D" }}
            onSubmit={(e) => { e.preventDefault(); go(q); }}
          >
            <div className="pl-3 md:pl-4 flex items-center text-[#8A8377]"><Search size={15} /></div>
            <input
              data-testid="ask-oracle-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="What do you want to investigate?"
              className="flex-1 bg-transparent px-2.5 md:px-3 py-3 md:py-3.5 text-[13px] md:text-[14px] text-[#E4DFD3] placeholder-[#5A554B] outline-none min-w-0"
            />
            <button
              type="submit"
              data-testid="ask-oracle-submit"
              className="px-3.5 md:px-6 font-mono text-[10px] uppercase tracking-[0.15em] md:tracking-[0.3em] text-[#D92D20] border-l hover:bg-[#D92D20] hover:text-[#0B0A0D] transition-colors shrink-0"
              style={{ borderColor: "#3A3630" }}
            >
              Query
            </button>
          </form>

          <div className="mt-4 flex gap-2 overflow-x-auto flex-nowrap md:flex-wrap -mx-5 px-5 md:mx-0 md:px-0 pb-1 [scrollbar-width:none]">
            {PROMPTS.map((p, i) => (
              <button
                key={p.id}
                data-testid={`oracle-prompt-${i}`}
                onClick={() => askDiscovery(p)}
                className={`border px-3 py-1.5 text-[11px] transition-colors text-left shrink-0 md:shrink whitespace-nowrap md:whitespace-normal ${
                  disc?.id === p.id ? "text-[#E4DFD3] border-[#D92D20]" : "text-[#A39E93] hover:text-[#E4DFD3] hover:border-[#D92D20]"
                }`}
                style={{ borderColor: disc?.id === p.id ? "#D92D20" : "#2D2A26", background: "#0B0A0D" }}
              >
                “{p.label}”
              </button>
            ))}
          </div>

          {/* Discovery answer — documented Archive relationships only */}
          {discLoading && (
            <div className="mt-4 font-mono text-[9px] uppercase tracking-[0.3em] text-[#7B7168]">
              // Consulting the record…
            </div>
          )}
          {disc && !discLoading && (
            <div
              className="mt-5 border p-4 md:p-5 motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300"
              style={{ borderColor: "#3A3630", background: "#0B0A0D" }}
              data-testid="oracle-discovery-panel"
            >
              <div className="font-mono text-[9px] uppercase tracking-[0.35em] text-[#A68F5E] mb-2">
                Oracle · {disc.question}
              </div>
              <p className="text-[13px] leading-relaxed text-[#CFC9BC]" data-testid="oracle-discovery-summary">
                {disc.summary_line}
              </p>
              {disc.connections?.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  {disc.connections.slice(0, 6).map((c, i) => (
                    <div key={i} className="flex items-baseline gap-2 text-[12px]" data-testid={`oracle-connection-${i}`}>
                      <span className="text-[#D92D20] font-mono text-[9px]">→</span>
                      <span className="text-[#A39E93]">
                        <Link to={`${GROUP_PATH[c.kind_group] || "/organizations"}/${c.entity_slug}`} className="text-[#E4DFD3] hover:text-[#D92D20]" style={{ borderBottom: "1px dotted #A68F5E66" }}>
                          {c.entity_name}
                        </Link>
                        {c.dossier_slug && (
                          <> — documented in{" "}
                            <Link to={`/topic/${c.dossier_slug}`} className="text-[#E4DFD3] hover:text-[#D92D20]" style={{ borderBottom: "1px dotted #A68F5E66" }}>
                              {c.dossier_title}
                            </Link>
                          </>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {disc.dossiers?.length > 0 && (
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                  {disc.dossiers.slice(0, 6).map((d, i) => (
                    <MiniRow key={d.slug} dossier={d} index={i} testid={`oracle-discovery-row-${i}`} />
                  ))}
                </div>
              )}
              <div className="mt-3 font-mono text-[8px] uppercase tracking-[0.25em] text-[#5A554C]">
                Answer derived from documented Archive relationships only
              </div>
            </div>
          )}
        </div>

        {recs.length > 0 && (
          <div className="mt-8 md:mt-10" data-testid="home-oracle-recommends">
            <SectionHeader
              file="File 005 · Assisted Discovery"
              title="Oracle Recommends"
              subtitle="Evidence-weighted picks from the published record — rotated daily."
            />
            <HRail testid="oracle-recommends-rail">
              {recs.map((d) => <RecCard key={d.slug} d={d} />)}
            </HRail>
            <Link
              to="/archive"
              className="mt-2 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.4em] text-[#A39E93] hover:text-[#E4DFD3] transition-colors border-b border-[#454038] pb-1"
              data-testid="oracle-recommends-all"
            >
              Explore the full archive <ArrowRight size={12} />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default AskOracle;
